import { useCallback, useMemo, useState } from 'react';
import { getCurrentBulkSettlementTime } from '../time';
import type {
  BulkSettlementMenuItem,
  BulkSettlementSelectedAddon,
  BulkSettlementMeridiem,
  BulkSettlementSelectedMenuItem,
  BulkSettlementTimeValue,
  BulkSettlementTransactionCardItem,
} from '../types';

const sanitizeTotalInput = (value: string): string => {
  const sanitizedValue = value.replace(/[^0-9.]/g, '');
  const [wholePart = '', ...decimalParts] = sanitizedValue.split('.');
  if (!decimalParts.length) return wholePart;
  return `${wholePart}.${decimalParts.join('')}`;
};

const getCardTotal = (total: string): number => {
  const parsed = Number(total);
  return Number.isFinite(parsed) ? parsed : 0;
};

const roundPrice = (value: number): number => Number(value.toFixed(0));

// Line ids only have to be unique within this editor session. A timestamp is
// not: two lines added in the same millisecond would collide, and `getUniqueID`
// is exactly a timestamp.
let lineSequence = 0;
const nextLineId = (): string => {
  lineSequence += 1;
  return `bulk-line-${lineSequence}`;
};

export const calculateSelectedItemsTotal = (items: BulkSettlementSelectedMenuItem[]): number =>
  items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

export const calculateSelectedItemsServiceCharge = (
  items: BulkSettlementSelectedMenuItem[],
  serviceChargePercentage: number,
): number => {
  const subtotal = items.reduce(
    (sum, item) => sum + (item.basePrice ?? item.unitPrice) * item.quantity,
    0,
  );
  return roundPrice((subtotal * serviceChargePercentage) / 100);
};

export const calculateSelectedItemsPaymentTotal = (
  items: BulkSettlementSelectedMenuItem[],
  serviceChargePercentage: number,
): number =>
  calculateSelectedItemsTotal(items) +
  calculateSelectedItemsServiceCharge(items, serviceChargePercentage);

export const updateSelectedMenuItems = ({
  selectedItems,
  item,
  quantityDelta,
}: {
  selectedItems: BulkSettlementSelectedMenuItem[];
  item: BulkSettlementMenuItem;
  quantityDelta: number;
}): BulkSettlementSelectedMenuItem[] => {
  const current = [...selectedItems];
  // Only lines WITHOUT components merge by menu id. A set line carries its own
  // choices, so two of them are two lines even for one menu item.
  const idx = current.findIndex(s => s.itemId === item._id && !s.addons?.length);

  if (idx >= 0) {
    const nextQty = current[idx].quantity + quantityDelta;
    if (nextQty <= 0) {
      current.splice(idx, 1);
    } else {
      current[idx] = { ...current[idx], quantity: nextQty };
    }
  } else if (quantityDelta > 0) {
    current.push({
      image: item.image,
      basePrice: Number(item.price ?? item.total_price ?? 0),
      itemId: item._id,
      itemName: item.item_name,
      itemNameJa: item.item_name_ja,
      lineId: nextLineId(),
      quantity: 1,
      unitPrice: Number(item.total_price ?? item.price ?? 0),
    });
  }

  return current;
};

/**
 * A set line: its own row, carrying the components chosen for it.
 *
 * Always appended, never merged into an existing line of the same menu item —
 * the same set with a different curry is a different price, and a merge would
 * bill one configuration twice. `lineTotal` is the picker's own figure (parent
 * gross + component grosses), which the server re-derives from the (group,
 * item) pairs when the transaction is settled.
 */
export const addSelectedSetLine = ({
  selectedItems,
  item,
  addons,
  lineTotal,
}: {
  selectedItems: BulkSettlementSelectedMenuItem[];
  item: BulkSettlementMenuItem;
  addons: BulkSettlementSelectedAddon[];
  lineTotal: number;
}): BulkSettlementSelectedMenuItem[] => [
  ...selectedItems,
  {
    addons,
    image: item.image,
    // The service charge is levied on the NET of the line, and the set's premium
    // is part of what was sold — so the base moves with the line, not with the
    // bare parent price.
    basePrice: lineTotal,
    itemId: item._id,
    itemName: item.item_name,
    itemNameJa: item.item_name_ja,
    lineId: nextLineId(),
    quantity: 1,
    unitPrice: lineTotal,
  },
];

// The stepper's minus for a set: drops that menu item's most recent line. Each
// line holds its own choices, so there is no quantity to decrement.
export const removeLastSelectedLine = (
  selectedItems: BulkSettlementSelectedMenuItem[],
  itemId: string,
): BulkSettlementSelectedMenuItem[] => {
  const lastIndex = selectedItems.map(entry => entry.itemId).lastIndexOf(itemId);
  if (lastIndex < 0) return selectedItems;
  return selectedItems.filter((_, index) => index !== lastIndex);
};

export const getSelectedItemCount = (items: BulkSettlementSelectedMenuItem[]): number =>
  items.reduce((sum, item) => sum + item.quantity, 0);

const createTransactionCard = ({
  cashierId,
  peopleCount,
}: {
  cashierId: string;
  peopleCount: string;
}): BulkSettlementTransactionCardItem => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  time: getCurrentBulkSettlementTime(),
  cashierId,
  itemType: 'bulk_item',
  peopleCount,
  paymentMethod: 'cash',
  paymentMethodId: '',
  methodName: null,
  total: '',
  selectedItems: [],
});

type Props = {
  currentCashierId: string;
  resolvedTableCapacity: number;
  serviceChargePercentage?: number;
};

export const useBulkSettlementTransactionCards = ({
  currentCashierId,
  resolvedTableCapacity,
  serviceChargePercentage = 0,
}: Props) => {
  const buildInitialCards = useCallback(
    (cashierId: string, tableCapacity: number) => [
      createTransactionCard({
        cashierId,
        peopleCount: tableCapacity > 0 ? String(tableCapacity) : '',
      }),
    ],
    [],
  );

  const [cards, setCards] = useState<BulkSettlementTransactionCardItem[]>(() => [
    createTransactionCard({
      cashierId: currentCashierId,
      peopleCount: resolvedTableCapacity > 0 ? String(resolvedTableCapacity) : '',
    }),
  ]);
  const [menuSelectorCardId, setMenuSelectorCardId] = useState('');
  const [selectedTimeCardId, setSelectedTimeCardId] = useState('');

  const totalAmount = useMemo(
    () => cards.reduce((sum, card) => sum + getCardTotal(card.total), 0),
    [cards],
  );

  const allCardsHavePositiveTotal = useMemo(
    () => cards.every(card => getCardTotal(card.total) > 0),
    [cards],
  );

  const activeMenuCard = useMemo(
    () => cards.find(card => card.id === menuSelectorCardId) ?? null,
    [cards, menuSelectorCardId],
  );

  const syncDefaultCardValues = useCallback(
    ({ cashierId, peopleCount }: { cashierId: string; peopleCount: string }) => {
      setCards(current =>
        current.map(card => ({
          ...card,
          cashierId: card.cashierId || cashierId,
          peopleCount: card.peopleCount || peopleCount,
        })),
      );
    },
    [],
  );

  const resetTransactions = useCallback(
    ({ cashierId, tableCapacity }: { cashierId: string; tableCapacity: number }) => {
      setCards(buildInitialCards(cashierId, tableCapacity));
      setMenuSelectorCardId('');
      setSelectedTimeCardId('');
    },
    [buildInitialCards],
  );

  const restoreCards = useCallback((savedCards: BulkSettlementTransactionCardItem[]) => {
    setCards(savedCards);
  }, []);

  const changeCard = useCallback(
    (
      cardId: string,
      field: 'time' | 'cashierId' | 'itemType' | 'peopleCount' | 'total',
      value: string | BulkSettlementTimeValue,
    ) => {
      setCards(current =>
        current.map(card =>
          card.id === cardId
            ? {
                ...card,
                [field]:
                  field === 'total' && typeof value === 'string'
                    ? sanitizeTotalInput(value)
                    : value,
                ...(field === 'itemType' ? { selectedItems: [], total: '' } : {}),
              }
            : card,
        ),
      );
    },
    [],
  );

  const changePaymentMethod = useCallback(
    (
      cardId: string,
      paymentMethod: string,
      paymentMethodId: string,
      methodName: string | null,
    ) => {
      setCards(current =>
        current.map(card =>
          card.id === cardId
            ? { ...card, methodName, paymentMethod, paymentMethodId }
            : card,
        ),
      );
    },
    [],
  );

  const addTransaction = useCallback(() => {
    if (!allCardsHavePositiveTotal) return;
    setCards(current => [
      ...current,
      createTransactionCard({
        cashierId: currentCashierId,
        peopleCount: resolvedTableCapacity > 0 ? String(resolvedTableCapacity) : '',
      }),
    ]);
  }, [allCardsHavePositiveTotal, currentCashierId, resolvedTableCapacity]);

  const deleteTransaction = useCallback((cardId: string) => {
    setCards(current => {
      if (current.length <= 1) return current;
      return current.filter(card => card.id !== cardId);
    });
  }, []);

  const openMenuSelector = useCallback((cardId: string) => {
    setMenuSelectorCardId(cardId);
  }, []);

  const closeMenuSelector = useCallback(() => {
    setMenuSelectorCardId('');
  }, []);

  const replaceMenuItems = useCallback(
    (cardId: string, selectedItems: BulkSettlementSelectedMenuItem[]) => {
      setCards(current =>
        current.map(card => {
          if (card.id !== cardId) return card;
          const total = calculateSelectedItemsPaymentTotal(selectedItems, serviceChargePercentage);
          return {
            ...card,
            selectedItems,
            total: total > 0 ? total.toFixed(2) : '',
          };
        }),
      );
    },
    [serviceChargePercentage],
  );

  const openTimePicker = useCallback((cardId: string) => {
    setSelectedTimeCardId(cardId);
  }, []);

  const closeTimePicker = useCallback(() => {
    setSelectedTimeCardId('');
  }, []);

  const selectTime = useCallback(
    (timeLabel: string) => {
      if (!selectedTimeCardId) return;
      const [timePart = '', meridiem = 'AM'] = timeLabel.split(' ');
      const [hour = '', minute = ''] = timePart.split(':');
      changeCard(selectedTimeCardId, 'time', {
        hour,
        meridiem: meridiem as BulkSettlementMeridiem,
        minute,
      });
    },
    [changeCard, selectedTimeCardId],
  );

  return {
    activeMenuCard,
    addTransaction,
    allCardsHavePositiveTotal,
    cards,
    changeCard,
    changePaymentMethod,
    closeMenuSelector,
    closeTimePicker,
    deleteTransaction,
    menuSelectorCardId,
    openMenuSelector,
    openTimePicker,
    replaceMenuItems,
    resetTransactions,
    restoreCards,
    selectTime,
    selectedTimeCardId,
    syncDefaultCardValues,
    totalAmount,
  };
};
